// Admin Customer API

import { apiClient } from './apiClient';
import type {
  AdminCustomerProfile,
  CustomerListQuery,
  CustomerListResponse,
  SegmentCounts,
  CustomerNoteData,
  CustomerTagData,
} from '../types/customer.types';

function buildQueryString(query: object): string {
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.append(key, String(value));
    }
  });
  const queryString = params.toString();
  return queryString ? `?${queryString}` : '';
}

export const adminCustomerApi = {
  async listCustomers(query: CustomerListQuery = {}): Promise<CustomerListResponse> {
    const queryString = buildQueryString(query);
    const response = await apiClient.get<CustomerListResponse>(
      `/api/admin/customers${queryString}`
    );
    return response.data;
  },

  async getCustomer(id: string): Promise<AdminCustomerProfile> {
    const response = await apiClient.get<{ success: boolean; data: AdminCustomerProfile }>(
      `/api/admin/customers/${id}`
    );
    return response.data.data;
  },

  async getSegmentCounts(): Promise<SegmentCounts> {
    const response = await apiClient.get<{ success: boolean; data: SegmentCounts }>(
      '/api/admin/customers/counts'
    );
    return response.data.data;
  },

  async updateCustomerStatus(id: string, isActive: boolean, reason?: string): Promise<void> {
    await apiClient.patch(`/api/admin/customers/${id}/status`, { isActive, reason });
  },

  async addNote(
    customerId: string,
    content: string,
    isImportant?: boolean
  ): Promise<CustomerNoteData> {
    const response = await apiClient.post<{ success: boolean; data: CustomerNoteData }>(
      `/api/admin/customers/${customerId}/notes`,
      { content, isImportant }
    );
    return response.data.data;
  },

  async deleteNote(customerId: string, noteId: string): Promise<void> {
    await apiClient.delete(`/api/admin/customers/${customerId}/notes/${noteId}`);
  },

  async addTag(customerId: string, tag: string, color?: string): Promise<CustomerTagData> {
    const response = await apiClient.post<{ success: boolean; data: CustomerTagData }>(
      `/api/admin/customers/${customerId}/tags`,
      { tag, color }
    );
    return response.data.data;
  },

  async removeTag(customerId: string, tag: string): Promise<void> {
    await apiClient.delete(
      `/api/admin/customers/${customerId}/tags/${encodeURIComponent(tag)}`
    );
  },

  async getAllTags(): Promise<string[]> {
    const response = await apiClient.get<{ success: boolean; data: string[] }>(
      '/api/admin/customers/tags'
    );
    return response.data.data;
  },
};
